import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus } from '@phosphor-icons/react/dist/ssr/Plus';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { COLORS, SHADOWS, BUTTON_TOKENS, CHAT_TOKENS, TYPOGRAPHY } from '../../styles/UISystem';
import { ALL_CONTACT_TYPES } from '../../utils/contactTypeConfig';
import { IconCircleButton } from './Icons/IconCircleButton';
import PillInput from './Forms/PillInput';
import { blackPill as blackPillStyle, whitePill as whitePillStyle } from './sharePillStyles';

/**
 * ShareSheet
 *
 * The Profile's one-surface opening move on a fresh connection (unified sheet,
 * mock 2026-07-18; composition per device pass 1, 2026-07-27): title "Share",
 * an optional greeting field pre-filled "Hi it's {firstName}!" (X-clear,
 * tap-to-edit — no autofocus so the keyboard doesn't bury the pills), the
 * venue sibling pill, then a vertical stack of one-tap TYPE pills
 * (icon + label, the person's filled channels), a chevron expanding the longer
 * tail, and the "+" circle opening the full multi-select editor ("Here is my
 * contact"). One tap = greet + share: every action callback passes the field's
 * current trimmed text so the caller posts the greeting bubble first, then the
 * share speech line. A cleared field means share only — no words in anyone's
 * mouth (contract #9b). PXP fresh rooms stage with greetingEnabled=false:
 * pills only (contract #9d).
 *
 * Black pill = the primary one-tap, recency-led (refine Q13): the content-named
 * last-share pill when eligible, else the first type pill (Phone). The Work
 * pill is deliberately absent — punted to the Profile epic (decision #4).
 *
 * No dimming backdrop — system-raised per the §4.2 scrim rule. The quiet
 * dismiss is a tap OUTSIDE the sheet (invisible backdrop; the X retired at
 * device pass 1, 2026-07-27 — every other sheet already closed this way);
 * dismissing abandons greeting and share both.
 *
 * @param {boolean} show
 * @param {boolean} greetingEnabled - render the greeting field (off for PXP)
 * @param {string} greetingPrefill - "Hi it's {full name}" (arrives async with
 *   own profile; synced in until the person edits)
 * @param {string|null} lastShareLabel - content label for the black last-share
 *   pill, or null (formatLastShareLabel)
 * @param {function} onLastShare - (greetingText) one-tap re-share of the stored
 *   last-share set
 * @param {function|null} onFullProfile - THREAD/PXP (plan 3 Step 10): when set,
 *   a black "Full profile" pill takes row one and the recall pill drops to row
 *   two. The host's explicit tap is what sends the full card (D4).
 *   The CALLER passes null when "Full profile" would carry nothing more than
 *   the single channel pill (D27) — a one-channel profile would otherwise show
 *   two buttons for one payload. Row one then falls through to primaryTypeId,
 *   so that pill becomes the black primary with no extra branch here.
 * @param {string[]} typeIds - the person's filled contact types, priority order
 *   (first 2 render collapsed; the rest behind the chevron)
 * @param {function} onTypeShare - (typeId, greetingText) one-tap single-channel share
 * @param {function} onMore - (greetingText) the "+" circle: opens
 *   SharingSelectionOverlay; the caller holds the text until editor completion
 * @param {function} onClose - outside-tap backdrop: quiet dismiss into the room
 * @param {boolean} disabled - in-flight guard: blocks taps, dims the pills
 * @param {string|null} venueLine - "Met at {venue}" under the greeting
 *   (exchange-clarity D5; device pass 1 round 4): rendered with the chat
 *   BANNER's feel — that's what it becomes in the room. Distinct roles wear
 *   distinct clothes: the greeting is a form field (chat-input geometry),
 *   the venue is a clickable banner pill; no pencil. Send = confirmation —
 *   the caller flips venueConfirmed only when this line was visible at send
 *   (the two-sheet sequence shows none; "Where you met" confirms there). PXP
 *   sheets show each side's OWN venue — per-user records. The caller
 *   snapshots at stage-time — no live Radar update mid-open.
 * @param {function} onVenueEdit - tap anywhere on the pill to edit (no icon —
 *   the in-chat Met container's owner's-tool rule); opens LocationOverlay
 *   (z 2100 — paints over this sheet)
 */

const COLLAPSED_COUNT = 2;

const ShareSheet = ({
  show,
  greetingEnabled = false,
  greetingPrefill = '',
  lastShareLabel = null,
  onLastShare,
  onFullProfile = null,
  typeIds = [],
  onTypeShare,
  onMore,
  onClose,
  disabled = false,
  venueLine = null,
  onVenueEdit,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [greeting, setGreeting] = useState(greetingPrefill);
  const [touched, setTouched] = useState(false);

  // The field resets to the pre-fill each time the sheet stages — a back-out
  // reopen drops edits (decision 3, accepted)
  useEffect(() => {
    if (show) setTouched(false);
  }, [show]);

  // Own profile loads async — sync the prefill in until the person edits;
  // never clobber their words after that
  useEffect(() => {
    if (!touched) setGreeting(greetingPrefill);
  }, [greetingPrefill, touched]);

  // What rides along with every action: the field's exact trimmed text, or
  // nothing when cleared/disabled (share only — no words in anyone's mouth)
  const greetingText = greetingEnabled ? greeting.trim() : '';

  const guard = (handler) => () => {
    if (!disabled && handler) handler();
  };

  // Shared treatment — UISystem/sharePillStyles is the one home (THREAD's two
  // sheets use the same rows).
  const blackPill = blackPillStyle(disabled);
  const whitePill = whitePillStyle(disabled);

  const visibleTypes = expanded ? typeIds : typeIds.slice(0, COLLAPSED_COUNT);
  const hasMoreTypes = !expanded && typeIds.length > COLLAPSED_COUNT;
  // Recency leads: the last-share pill is the black primary when present,
  // otherwise the first type pill takes it
  // ...unless "Full profile" holds row one (THREAD), in which case no type
  // pill is black — there is exactly one primary.
  const primaryTypeId = lastShareLabel || onFullProfile ? null : typeIds[0];

  const typePill = (typeId) => {
    const config = ALL_CONTACT_TYPES[typeId];
    if (!config) return null;
    const isPrimary = typeId === primaryTypeId;
    const Icon = config.Icon;
    return (
      <button
        key={typeId}
        aria-label={`Share ${config.label}`}
        onClick={guard(() => onTypeShare?.(typeId, greetingText))}
        style={isPrimary ? blackPill : whitePill}
      >
        <Icon
          size={20}
          color={isPrimary ? '#FFFFFF' : BUTTON_TOKENS.ACTION_TEXT_SECONDARY}
          weight="regular"
        />
        {config.label}
      </button>
    );
  };

  return (
    <AnimatePresence>
      {/* Invisible backdrop — the quiet dismiss (tap outside closes, no dim
          per §4.2). A SIBLING of the sheet, not a child: the sheet's y
          transform would trap a fixed child inside its containing block.
          Plain div — animating opacity on a transparent element was a no-op
          (/simplify); it unmounts at dismissal start, which is fine (the
          sheet is already leaving). */}
      {show && (
        <div
          onClick={guard(onClose)}
          style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          aria-hidden="true"
        />
      )}
      {show && (
        <motion.div
          key="share-sheet"
          initial={{ y: '100%' }}
          animate={{ y: 0, transition: { type: 'spring', damping: 30, stiffness: 300 } }}
          exit={{ y: '100%', transition: { duration: 0.3, ease: 'easeIn' } }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: '#FFFFFF',
            borderRadius: '24px 24px 0 0',
            boxShadow: SHADOWS.TIER3,
            padding: '28px 20px calc(32px + env(safe-area-inset-bottom))',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Title (the X retired at device pass 1 — outside tap dismisses) */}
          <div
            style={{
              fontSize: '20px',
              fontWeight: 700,
              color: COLORS.CHAT_TEXT_DARK,
              textAlign: 'center',
              width: '100%',
              marginBottom: '24px',
            }}
          >
            Share
          </div>

          {/* Greeting field — pre-filled real text, X clears, rides along with
              the share; no autofocus (the keyboard would bury the pills).
              Rendered EXACTLY as the chat input, because that's what it is
              (device pass 1 round 4, user): PillInput's bordered default IS
              the composer geometry — border, 25px radius, minHeight 50,
              left-aligned 20px body text. */}
          {greetingEnabled && (
            <div style={{ width: '100%', maxWidth: '420px', marginBottom: '14px' }}>
              <PillInput
                value={greeting}
                disabled={disabled}
                onChange={(e) => {
                  setTouched(true);
                  setGreeting(e.target.value);
                }}
              >
                {greeting && (
                  <button
                    aria-label="Clear greeting"
                    onClick={guard(() => {
                      setTouched(true);
                      setGreeting('');
                    })}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                      flexShrink: 0,
                    }}
                  >
                    <X size={18} color="#AEAEB2" weight="regular" />
                  </button>
                )}
              </PillInput>
            </div>
          )}

          {/* Venue pill (D5; device pass 1 round 4, user) — the chat BANNER's
              feel, because that's what this becomes in the room: same surface
              (white, system shadow, 16px radius, 8/14 padding), content-
              hugging and centered like the banner sits in the chat. Text
              rides the sheet-wide 20px (the in-chat banner stays 16 — feel
              transfers, the sheet keeps one size). Whole pill taps to edit,
              no icon (owner's-tool rule). Sending with this visible confirms
              it. */}
          {venueLine && (
            <div
              onClick={guard(onVenueEdit)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                maxWidth: '90%',
                backgroundColor: CHAT_TOKENS.SYSTEM_BG,
                borderRadius: '16px',
                padding: '8px 14px',
                boxShadow: CHAT_TOKENS.SYSTEM_SHADOW,
                marginBottom: '20px',
                cursor: disabled ? 'default' : 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <span
                style={{
                  fontSize: TYPOGRAPHY.BODY_REGULAR.fontSize,
                  fontWeight: 500,
                  color: COLORS.CHAT_TEXT_DARK,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {venueLine}
              </span>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '14px',
              opacity: disabled ? 0.6 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            {/* First row: + circle | first pill | invisible balance spacer,
                so the pill stack stays centered in the sheet */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <IconCircleButton
                icon={Plus}
                buttonSize={40}
                size={20}
                backgroundColor="#FFFFFF"
                iconColor="#8E8E93"
                iconWeight="regular"
                onClick={guard(() => onMore?.(greetingText))}
                aria-label="More share options"
              />
              {onFullProfile ? (
                /* THREAD (plan 3 Step 10, profile_2): "Full profile" IS the
                   black primary — the host's deliberate tap is what sends the
                   full card, which is why THREAD needs no auto-broadcast and
                   keeps the stricter first-name+photo formation payload (D4).
                   The recall pill is demoted to the second row. */
                <button
                  aria-label="Share full profile"
                  onClick={guard(() => onFullProfile?.(greetingText))}
                  style={blackPill}
                >
                  Full profile
                </button>
              ) : lastShareLabel ? (
                <button
                  aria-label="Share last share"
                  onClick={guard(() => onLastShare?.(greetingText))}
                  style={blackPill}
                >
                  {lastShareLabel}
                </button>
              ) : (
                typePill(visibleTypes[0])
              )}
              <div style={{ width: '40px', flexShrink: 0 }} />
            </div>

            {/* Second row under Full profile: the recall pill, when eligible */}
            {onFullProfile && lastShareLabel && (
              <button
                aria-label="Share last share"
                onClick={guard(() => onLastShare?.(greetingText))}
                style={whitePill}
              >
                {lastShareLabel}
              </button>
            )}

            {/* Remaining pills (all of them when row one was taken by the
                recall pill or Full profile) */}
            {(lastShareLabel || onFullProfile ? visibleTypes : visibleTypes.slice(1)).map(typePill)}
          </div>

          {/* Chevron — expands the longer tail of filled channels (replaces the
              "More" text button, mock 2026-07-18; revert if it reads as dismiss
              on device — decision 7) */}
          {hasMoreTypes && (
            <button
              aria-label="Show more channels"
              onClick={guard(() => setExpanded(true))}
              style={{
                marginTop: '20px',
                padding: '8px 24px',
                background: 'none',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: disabled ? 'default' : 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <CaretDown size={20} color={BUTTON_TOKENS.ACTION_TEXT_SECONDARY} weight="regular" />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

ShareSheet.propTypes = {
  show: PropTypes.bool,
  greetingEnabled: PropTypes.bool,
  greetingPrefill: PropTypes.string,
  lastShareLabel: PropTypes.string,
  onLastShare: PropTypes.func,
  onFullProfile: PropTypes.func,
  typeIds: PropTypes.arrayOf(PropTypes.string),
  onTypeShare: PropTypes.func,
  onMore: PropTypes.func,
  onClose: PropTypes.func,
  disabled: PropTypes.bool,
  venueLine: PropTypes.string,
  onVenueEdit: PropTypes.func,
};

export default ShareSheet;
